// hooks/useCustomPromo.ts
import { useEffect, useState } from "react";
import { isPresent } from "src/utils/checks";
import { isPromoValid } from "components/CustomPromo/promoValidation";
import { useRemotePromo } from "src/ssc-api/useRemotePromos";
import { useLocation } from "@gatsbyjs/reach-router";
import { checkIfLocalStorageIsSupportedByBrowser } from "src/utils/local-storage-item";

export type CustomPromoState = {
  showCustomPromoModal: boolean;
  promoCode: string;
};

const STORAGE_KEY = "customPromoState";
const localStorageIsAvailable = checkIfLocalStorageIsSupportedByBrowser();
const getPromoCodeFromUrl = (search: string) => {
  const params = new URLSearchParams(search);
  const promoCode = params.get("pc");

  return promoCode;
};

export const useCustomPromo = () => {
  const [promoState, setPromoState] = useState<CustomPromoState>({
    showCustomPromoModal: false,
    promoCode: ""
  });

  const location = useLocation();

  const remotePromo = useRemotePromo(
    promoState.promoCode.length > 0
      ? promoState.promoCode
      : (getPromoCodeFromUrl(location.search) ?? "")
  );

  useEffect(() => {
    if (!localStorageIsAvailable) {
      return;
    }
    const promoCodeFromUrl = getPromoCodeFromUrl(location.search);
    if (isPresent(promoCodeFromUrl)) {
      localStorage.removeItem(STORAGE_KEY);
      const newState = {
        showCustomPromoModal: true,
        promoCode: promoCodeFromUrl
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
      setPromoState(newState);
    } else {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        setPromoState(parsedState);
      }
    }
  }, [location.search]);

  const updatePromoState = (updates: Partial<CustomPromoState>) => {
    if (!localStorageIsAvailable) {
      return;
    }
    const newState = { ...promoState, ...updates };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
    setPromoState(newState);
  };

  const promoIsValid =
    isPresent(remotePromo.data) &&
    isPromoValid(promoState.promoCode, remotePromo.data.toDate);

  return {
    promoState,
    updatePromoState,
    remotePromo,
    promoIsValid
  };
};
